/**
 * Page édition projet — `/projects/:id/edit` (refonte 9.P.3.B parité Companies R1+R2).
 *
 * Pattern UX commun :
 *   - sticky page header `top-16 lg:top-0` + breadcrumb + H1 + status pill
 *   - layout 2-col `lg:grid-cols-[14rem_1fr]` : section nav left + form right
 *   - CardSection moderne par fieldset
 *   - sticky save bar bottom (Cancel + Save) toujours visible
 *   - useUnsavedChangesGuard global dirty (popup modif non sauvegardées)
 *   - allowNavigation() bypass single-shot avant navigate post-save success
 *   - Archived = read-only : `useEffect` defensive redirect /:id si status='archived'
 *     (cohérent Companies — restore d'abord pour modifier)
 *
 * D-A7 : on n'envoie que les champs définis (filtre `=== undefined`),
 * pas de `null` pour effacer (UpdateProjectSchema ne l'accepte pas pour la
 * plupart des champs). Limitation documentée 8.D — broker_id / contact_id
 * acceptent `null` mais le formulaire actuel n'expose pas ce détachement.
 */

import type { GuaranteeCode, ProjectRow, UpdateProjectInput } from "@kollectivia/shared";
import { PROJECT_STATUSES } from "@kollectivia/shared/constants";
import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { GuaranteeMultiSelect } from "../../components/GuaranteeMultiSelect";
import { InsurerMultiSelect } from "../../components/InsurerMultiSelect";
import { TagsInput } from "../../components/TagsInput";
import { ApiException } from "../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../lib/apiErrorMessage";
import { extractApiError, FieldError, FormErrorBanner } from "../../lib/formErrors";
import { useProject, useUpdateProject } from "../../lib/queries/projects";
import { toast } from "../../lib/toast";
import { useUnsavedChangesGuard } from "../../lib/unsavedChanges/UnsavedChangesContext";
import { CompanySectionNav } from "../companies/_components/CompanySectionNav";

type FormState = {
  title: string;
  reference: string;
  status: ProjectRow["status"];
  stage_label: string;
  progress: string;
  effect_date: string;
  renewal_target_date: string;
  deadline_date: string;
  insurer_codes: string[];
  guarantee_codes: GuaranteeCode[];
  notes: string;
  tags: string[];
};

function fromProject(p: ProjectRow): FormState {
  return {
    title: p.title,
    reference: p.reference ?? "",
    status: p.status,
    stage_label: p.stage_label ?? "",
    progress: p.progress != null ? String(p.progress) : "",
    effect_date: p.effect_date ?? "",
    renewal_target_date: p.renewal_target_date ?? "",
    deadline_date: p.deadline_date ?? "",
    insurer_codes: p.insurer_codes,
    guarantee_codes: p.guarantee_codes as GuaranteeCode[],
    notes: p.notes ?? "",
    tags: p.tags,
  };
}

function toPayload(s: FormState): UpdateProjectInput {
  const str = (v: string) => (v.trim() === "" ? undefined : v.trim());
  const num = (v: string) => {
    const t = v.trim();
    if (t === "") return undefined;
    const n = Number(t);
    return Number.isFinite(n) ? n : undefined;
  };
  return {
    title: s.title.trim(),
    reference: str(s.reference),
    status: s.status,
    stage_label: str(s.stage_label),
    progress: num(s.progress),
    effect_date: str(s.effect_date),
    renewal_target_date: str(s.renewal_target_date),
    deadline_date: str(s.deadline_date),
    insurer_codes: s.insurer_codes,
    guarantee_codes: s.guarantee_codes,
    notes: str(s.notes),
    tags: s.tags,
  };
}

export function ProjectEditPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const navigate = useNavigate();
  const project = useProject(id);
  const update = useUpdateProject(id ?? "");

  const [form, setForm] = useState<FormState | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (project.data && form === null) setForm(fromProject(project.data));
  }, [project.data, form]);

  // 9.P.2.R2 guard — appelé AVANT tout early return (rules-of-hooks).
  const { allowNavigation } = useUnsavedChangesGuard(
    dirty,
    form ? t("projects.edit.unsaved_message", { title: form.title }) : "",
  );

  // Archived = read-only (parité Companies). Defensive redirect /:id si archived.
  useEffect(() => {
    if (project.data && project.data.status === "archived" && id) {
      toast.info(t("projects.edit.archived_blocked"));
      allowNavigation();
      navigate(`/projects/${id}`, { replace: true });
    }
  }, [project.data, id, navigate, t, allowNavigation]);

  if (project.isLoading || !form) {
    return (
      <div className="mx-auto w-full max-w-4xl">
        <div className="h-32 rounded-xl bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
      </div>
    );
  }
  if (project.error) {
    return (
      <div className="mx-auto w-full max-w-4xl">
        <div
          role="alert"
          className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          {project.error instanceof ApiException
            ? getApiErrorMessage(project.error, lang) || t("errors.generic")
            : t("errors.generic")}
        </div>
      </div>
    );
  }

  const setField = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((s) => (s ? { ...s, [k]: v } : s));
    setDirty(true);
  };

  const onCancel = () => {
    if (dirty && !confirm(t("projects.edit.cancel_confirm"))) return;
    navigate(`/projects/${id}`);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !id) return;
    try {
      await update.mutateAsync(toPayload(form));
      // 9.P.2.R2 fix : bypass single-shot avant navigate post-save (anti race batching).
      allowNavigation();
      setDirty(false);
      navigate(`/projects/${id}`);
    } catch {
      /* visible via update.error */
    }
  };

  const { apiError, fieldErrors } = extractApiError(update.error);

  const sectionNavItems = [
    { anchorId: "identity", label: t("projects.sections.identity") },
    { anchorId: "dates", label: t("projects.sections.dates") },
    { anchorId: "coverage", label: t("projects.sections.coverage") },
    { anchorId: "notes", label: t("projects.sections.notes") },
  ];

  return (
    <div className="mx-auto w-full max-w-screen-xl">
      {/* Sticky header */}
      <header className="sticky top-16 z-10 -mx-4 border-b border-slate-200 bg-slate-50/95 px-4 py-4 backdrop-blur-sm lg:top-0 lg:-mx-8 lg:px-8 lg:py-5">
        <nav aria-label="breadcrumb" className="mb-2 text-sm">
          <button
            type="button"
            onClick={onCancel}
            className="focus-visible:ring-brand-500 rounded text-slate-600 hover:text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            ← {form.title}
          </button>
        </nav>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          {t("projects.edit.title")}
        </h1>
      </header>

      <FormErrorBanner apiError={apiError} fieldErrors={fieldErrors} />

      <form onSubmit={onSubmit} className="py-6 lg:grid lg:grid-cols-[14rem_1fr] lg:gap-8 lg:py-8">
        {/* Section nav left (desktop only) */}
        <aside className="hidden lg:block">
          <div className="sticky top-32">
            <CompanySectionNav
              items={sectionNavItems}
              ariaLabel={t("projects.edit.section_nav_label")}
            />
          </div>
        </aside>

        {/* Form right column */}
        <div className="space-y-5">
          <FormCard id="identity" title={t("projects.sections.identity")}>
            <div className="space-y-4">
              <label className="block">
                <span className="label">{t("projects.fields.title")} *</span>
                <input
                  className="input"
                  value={form.title}
                  onChange={(e) => setField("title", e.target.value)}
                  required
                />
                <FieldError errors={fieldErrors} name="title" />
              </label>
              <label className="block">
                <span className="label">{t("projects.fields.reference")}</span>
                <input
                  className="input"
                  value={form.reference}
                  onChange={(e) => setField("reference", e.target.value)}
                />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="label">{t("projects.fields.status")}</span>
                  <select
                    className="input"
                    value={form.status}
                    onChange={(e) => setField("status", e.target.value as ProjectRow["status"])}
                  >
                    {PROJECT_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {t(`projects.statuses.${s}`)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="label">{t("projects.fields.progress")}</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    className="input"
                    value={form.progress}
                    onChange={(e) => setField("progress", e.target.value)}
                  />
                </label>
              </div>
              <label className="block">
                <span className="label">{t("projects.fields.stage_label")}</span>
                <input
                  className="input"
                  value={form.stage_label}
                  onChange={(e) => setField("stage_label", e.target.value)}
                />
              </label>
            </div>
          </FormCard>

          <FormCard id="dates" title={t("projects.sections.dates")}>
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="block">
                <span className="label">{t("projects.fields.effect_date")}</span>
                <input
                  type="date"
                  className="input"
                  value={form.effect_date}
                  onChange={(e) => setField("effect_date", e.target.value)}
                />
              </label>
              <label className="block">
                <span className="label">{t("projects.fields.renewal_target_date")}</span>
                <input
                  type="date"
                  className="input"
                  value={form.renewal_target_date}
                  onChange={(e) => setField("renewal_target_date", e.target.value)}
                />
              </label>
              <label className="block">
                <span className="label">{t("projects.fields.deadline_date")}</span>
                <input
                  type="date"
                  className="input"
                  value={form.deadline_date}
                  onChange={(e) => setField("deadline_date", e.target.value)}
                />
              </label>
            </div>
          </FormCard>

          <FormCard id="coverage" title={t("projects.sections.coverage")}>
            <div className="space-y-4">
              <div>
                <span className="label">{t("projects.edit.planned_insurers_optional")}</span>
                <InsurerMultiSelect
                  value={form.insurer_codes}
                  onChange={(next) => setField("insurer_codes", next)}
                />
                <FieldError errors={fieldErrors} name="insurer_codes" />
              </div>
              <div>
                <span className="label">{t("projects.fields.guarantee_codes")}</span>
                <GuaranteeMultiSelect
                  value={form.guarantee_codes}
                  onChange={(next) => setField("guarantee_codes", next)}
                />
              </div>
            </div>
          </FormCard>

          <FormCard id="notes" title={t("projects.sections.notes")}>
            <div className="space-y-4">
              <label className="block">
                <span className="label">{t("projects.edit.internal_notes")}</span>
                <textarea
                  className="input min-h-[6rem]"
                  value={form.notes}
                  onChange={(e) => setField("notes", e.target.value)}
                />
              </label>
              <div>
                <span className="label">{t("projects.fields.tags")}</span>
                <TagsInput value={form.tags} onChange={(next) => setField("tags", next)} />
              </div>
            </div>
          </FormCard>

          {/* Sticky save bar */}
          <div className="sticky bottom-0 z-10 -mx-4 border-t border-slate-200 bg-white/95 px-4 py-3 backdrop-blur-sm lg:-mx-8 lg:px-8">
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onCancel}
                disabled={update.isPending}
                className="focus-visible:ring-brand-500 touch-manipulation rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 touch-manipulation rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
                disabled={update.isPending || !form.title.trim()}
              >
                {update.isPending ? t("app.loading") : t("common.save")}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

function FormCard({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-32 rounded-xl border border-slate-200 bg-white shadow-sm">
      <header className="border-b border-slate-100 px-5 py-3.5">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
      </header>
      <div className="px-5 py-5">{children}</div>
    </section>
  );
}
